import React, {useEffect, useMemo, useState} from "react";
import ko from "date-fns/locale/ko";
import moment from "moment";
import styled from "styled-components";
import {CustomDatePicker} from "../../assets/GlobalStyles";
import {
  getLastDay,
  getLastMonth,
  getLastNinetyDay,
  getLastThirtyDay,
  getLastWeekDay,
  getThisMonth,
  getToDay,
} from "../../common/DateUtils";

const presets = [
  {key: "thisMonth", label: "이번달"},
  {key: "lastMonth", label: "지난달"},
  {key: "today", label: "오늘"},
  {key: "lastDay", label: "어제"},
  {key: "lastWeekDay", label: "지난 7일"},
  {key: "lastThirtyDay", label: "지난 30일"},
  {key: "lastNinetyDay", label: "지난 90일"},
];

const getPresetRange = key => {
  const ranges = {
    thisMonth: getThisMonth(),
    lastMonth: getLastMonth(),
    today: {startDay: getToDay(), endDay: getToDay()},
    lastDay: {startDay: getLastDay(), endDay: getLastDay()},
    lastWeekDay: getLastWeekDay(),
    lastThirtyDay: getLastThirtyDay(),
    lastNinetyDay: getLastNinetyDay(),
  };
  return ranges[key];
};

export default function DashboardDateSelector({value, onChange, showIcon = true}) {
  const [activePreset, setActivePreset] = useState("thisMonth");
  const dateRange = useMemo(
    () => [
      value?.startDate ? new Date(`${value.startDate}T00:00:00`) : null,
      value?.endDate ? new Date(`${value.endDate}T00:00:00`) : null,
    ],
    [value?.endDate, value?.startDate]
  );
  const [startDate, endDate] = dateRange;

  useEffect(() => {
    const matchedPreset = presets.find(({key}) => {
      const range = getPresetRange(key);
      return range?.startDay === value?.startDate && range?.endDay === value?.endDate;
    });
    setActivePreset(matchedPreset?.key ?? "");
  }, [value?.endDate, value?.startDate]);

  const updateRange = dates => {
    const [nextStartDate, nextEndDate] = dates;
    setActivePreset("");
    onChange({
      startDate: nextStartDate ? moment(nextStartDate).format("YYYY-MM-DD") : "",
      endDate: nextEndDate ? moment(nextEndDate).format("YYYY-MM-DD") : "",
    });
  };

  const selectPreset = key => {
    const selectedRange = getPresetRange(key);
    if (!selectedRange) {
      return;
    }
    setActivePreset(key);
    onChange({
      startDate: selectedRange.startDay,
      endDate: selectedRange.endDay,
    });
  };

  return (
    <DatePickerWrap>
      {showIcon && <CalendarIcon aria-hidden="true">
        <svg viewBox="0 0 24 24">
          <path fill="currentColor" d="M7 2h2v2h6V2h2v2h3v18H4V4h3V2Zm11 8H6v10h12V10ZM6 6v2h12V6H6Z"/>
        </svg>
      </CalendarIcon>}
      <StyledDatePicker
        $showIcon={showIcon}
        selectsRange
        startDate={startDate}
        endDate={endDate}
        minDate={new Date(getLastNinetyDay().startDay)}
        maxDate={new Date()}
        onChange={updateRange}
        dateFormat="yyyy-MM-dd"
        locale={ko}
        isClearable={false}
        monthsShown={2}
        openToDate={startDate}
        focusSelectedMonth
        placeholderText="조회기간 선택"
        calendarContainer={({className, children}) => (
          <CalendarPanel className={className}>
            <CalendarBody>{children}</CalendarBody>
            <PresetPanel>
              <PresetTitle>빠른 기간 선택</PresetTitle>
              {presets.map(preset => (
                <PresetButton
                  key={preset.key}
                  type="button"
                  $active={activePreset === preset.key}
                  onClick={() => selectPreset(preset.key)}
                >
                  {preset.label}
                </PresetButton>
              ))}
            </PresetPanel>
          </CalendarPanel>
        )}
      />
    </DatePickerWrap>
  );
}

const DatePickerWrap = styled.div`
  position: relative;
  width: 270px;

  @media (max-width: 640px) {
    width: 100%;
  }

  .react-datepicker-wrapper {
    width: 100%;
  }
`;

const CalendarIcon = styled.span`
  position: absolute;
  top: 50%;
  left: 11px;
  z-index: 2;
  display: inline-flex;
  color: #7c8795;
  pointer-events: none;
  transform: translateY(-50%);

  svg {
    width: 15px;
    height: 15px;
  }
`;

const StyledDatePicker = styled(CustomDatePicker)`
  width: 100%;
  height: 36px;
  box-sizing: border-box;
  padding: ${({$showIcon}) => $showIcon ? "0 12px 0 35px" : "0 8px"};
  border: 1px solid #dfe3e7;
  border-radius: 7px;
  background: var(--color-surface);
  color: #4b5563;
  font-size: var(--font-size-12);
  outline: none;

  &:hover {
    border-color: var(--color-primary-border);
  }

  &:focus {
    border-color: ${({theme}) => theme.color.subColor};
    box-shadow: 0 0 0 3px rgba(59, 130, 246, .1);
  }
`;

const CalendarPanel = styled.div`
  display: flex !important;
  flex-direction: row;
  align-items: stretch;
  overflow: hidden;
  border: 1px solid #dfe4ea !important;
  border-radius: 10px !important;
  background: var(--color-surface);
  box-shadow: 0 16px 40px rgba(23, 32, 45, .16) !important;
  font-family: inherit !important;

  .react-datepicker__navigation {
    top: 9px;
    z-index: 3;
    width: 30px;
    height: 30px;
    border-radius: 6px;
  }

  .react-datepicker__navigation:hover {
    background: var(--color-primary-soft);
  }

  .react-datepicker__navigation--previous {
    left: 8px;
  }

  .react-datepicker__navigation--next {
    right: 114px;
  }

  @media (max-width: 720px) {
    max-width: calc(100vw - 28px);

    .react-datepicker__month-container:nth-of-type(2) {
      display: none;
    }
  }
`;

const PresetPanel = styled.div`
  display: flex;
  width: 104px;
  flex: 0 0 104px;
  flex-direction: column;
  gap: 3px;
  padding: 12px 8px;
  border-left: 1px solid #e8edf2;
  background: #f8fafc;

  @media (max-width: 440px) {
    width: 88px;
    flex-basis: 88px;
    padding: 10px 6px;
  }
`;

const PresetTitle = styled.div`
  padding: 3px 8px 7px;
  color: #7a8491;
  font-size: var(--font-size-11);
  font-weight: 700;
  white-space: nowrap;
`;

const PresetButton = styled.button`
  min-height: 30px;
  padding: 0 8px;
  border: 1px solid ${({$active}) => ($active ? "var(--color-primary-focus)" : "transparent")};
  border-radius: 6px;
  background: ${({$active}) => ($active ? "var(--color-primary-soft)" : "transparent")};
  color: ${({$active, theme}) => ($active ? theme.color.subColor : "#67717e")};
  font-size: var(--font-size-11);
  font-weight: ${({$active}) => ($active ? 700 : 500)};
  text-align: left;

  &:hover {
    background: var(--color-primary-soft);
    color: ${({theme}) => theme.color.subColor};
  }
`;

const CalendarBody = styled.div`
  display: flex;
  min-width: 0;

  .react-datepicker {
    display: flex;
    border: 0;
    font-family: inherit;
  }

  .react-datepicker__month-container {
    float: none;
    flex: 0 0 auto;
  }

  .react-datepicker__header {
    border-bottom-color: var(--color-divider);
    background: var(--color-surface);
  }

  .react-datepicker__day--selected,
  .react-datepicker__day--in-range,
  .react-datepicker__day--range-start,
  .react-datepicker__day--range-end {
    background: var(--color-primary);
    color: var(--color-surface);
  }

  .react-datepicker__day--in-selecting-range {
    background: var(--color-primary-muted);
    color: var(--color-main);
  }
`;
